import React, { useState, useEffect } from 'react';
import { auth, functions } from '../firebase/firebase';
import { reload } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Mail, CheckCircle2, RotateCw, ArrowLeft } from 'lucide-react';

const EmailVerificationPendingModal = ({ open, onClose, email, onVerified }) => {
  const [message, setMessage] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [checking, setChecking] = useState(false);

  // Auto-check verification status every 3 seconds
  useEffect(() => {
    if (!open) return;

    const checkVerification = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        setChecking(true);
        await reload(user);
        
        if (user.emailVerified) {
          console.log('✅ Email verified!');
          setMessage('Email verified successfully!');
          // Call onVerified callback after a short delay
          setTimeout(() => {
            if (onVerified) onVerified();
          }, 1000);
        }
      } catch (error) {
        console.error('Error checking verification:', error);
      } finally {
        setChecking(false);
      }
    };

    // Initial check
    checkVerification();

    // Check every 3 seconds
    const interval = setInterval(checkVerification, 3000);

    return () => clearInterval(interval);
  }, [open, onVerified]);

  const handleResendEmail = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setResendDisabled(true);
    setMessage('');

    try {
      const sendVerification = httpsCallable(functions, 'sendEmailVerificationCustom');
      await sendVerification();
      setMessage('Verification email resent!');
    } catch (error) {
      console.error('Error resending verification email:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      // Re-enable after 5 seconds to prevent spamming
      setTimeout(() => {
        setResendDisabled(false);
        setMessage('');
      }, 5000);
    }
  };

  const handleBackToSignup = async () => {
    // Sign out and close modal
    await auth.signOut();
    if (onClose) onClose();
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '480px',
        width: '90%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px'
      }}>
        {/* Header Icon */}
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--branding-app-primary) 0%, var(--branding-app-primary-hover) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
        }}>
          <Mail size={48} color="#fff" />
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: 'var(--cc-text-primary)'
          }}>
            Verify your email
          </h2>
          
          {email && (
            <div style={{ marginTop: '16px' }}>
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                color: 'var(--cc-text-muted)'
              }}>
                We've sent a verification email to:
              </p>
              <p style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '500',
                color: 'var(--cc-text-primary)',
                wordBreak: 'break-word'
              }}>
                {email}
              </p>
              <p style={{
                margin: '8px 0 0 0',
                fontSize: '12px',
                color: '#f59e0b',
                fontWeight: '500'
              }}>
                Don't see it? Check your spam/junk folder
              </p>
            </div>
          )}
        </div>

        {/* Status Message */}
        {message && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            background: message.includes('Error') ? '#fee2e2' : '#d1fae5',
            color: message.includes('Error') ? '#dc2626' : '#059669',
            fontSize: '14px',
            textAlign: 'center',
            width: '100%'
          }}>
            {message}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          marginTop: '8px'
        }}>
          <button
            onClick={async () => {
              const user = auth.currentUser;
              if (!user) return;
              
              setChecking(true);
              try {
                await reload(user);
                if (user.emailVerified) {
                  setMessage('Email verified successfully!');
                  if (onVerified) {
                    setTimeout(() => onVerified(), 1000);
                  }
                } else {
                  setMessage('Email not verified yet. Please check your inbox and click the verification link.');
                }
              } catch (error) {
                setMessage(`Error: ${error.message}`);
              } finally {
                setChecking(false);
              }
            }}
            disabled={checking}
            style={{
              padding: '14px 24px',
              background: 'var(--branding-app-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: checking ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: checking ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!checking) e.target.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              if (!checking) e.target.style.opacity = '1';
            }}
          >
            <CheckCircle2 size={20} />
            {"I've verified my email"}
          </button>

          <button
            onClick={handleResendEmail}
            disabled={resendDisabled}
            style={{
              padding: '14px 24px',
              background: 'var(--branding-app-surface)',
              color: 'var(--cc-text-primary)',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: resendDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: resendDisabled ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!resendDisabled) {
                e.target.style.background = '#e2e8f0';
              }
            }}
            onMouseLeave={(e) => {
              if (!resendDisabled) {
                e.target.style.background = 'var(--branding-app-surface)';
              }
            }}
          >
            <RotateCw size={18} />
            Resend email
          </button>
        </div>

        {/* Back to Sign Up Button */}
        <button
          onClick={handleBackToSignup}
          style={{
            marginTop: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--branding-app-primary)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'var(--branding-app-primary-hover)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'var(--branding-app-primary)';
          }}
        >
          <ArrowLeft size={16} />
          Back to Sign Up
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationPendingModal;

